import { reportErrorLog } from "./backend_helper";
import { getStorageUser } from "./api_helper";

/**
 * Reporte de crashes y errores JS al backend (`POST /error-logs`).
 *
 * Reglas de oro de este modulo:
 *
 *  1. NUNCA lanza. Se invoca desde componentDidCatch y desde los listeners de
 *     window, es decir, en momentos en los que la aplicacion ya esta rota. Un
 *     throw aqui convertiria un crash diagnosticable en uno mudo.
 *  2. NUNCA usa post() de api_helper: ese dispara toasts y, ante un 401, borra
 *     la sesion y redirige a /login. Se usa postQuiet a traves de
 *     reportErrorLog.
 *  3. Deduplica en memoria. Un crash de render se repite en bucle; sin esto la
 *     tabla se llena con la misma fila. El backend agrupa ademas por
 *     fingerprint dentro de una ventana de 10 minutos.
 */

/** Fingerprints ya reportados en esta carga de pagina. */
const reported = new Set();

/** Techo local: si algo se desboca, se deja de enviar. */
const MAX_REPORTS_PER_SESSION = 20;
let sent = 0;

let handlersInstalled = false;

const safeString = (value) => {
  try {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message || String(value);
    return String(value);
  } catch (e) {
    return "";
  }
};

/** Mensaje legible a partir de cualquier cosa que llegue como "error". */
const messageOf = (error) => {
  const message = safeString(error?.message) || safeString(error);
  return message.slice(0, 1000) || "Error desconocido";
};

const fingerprintOf = (origin, message) => {
  try {
    return `${origin}|${message}|${window.location?.pathname ?? ""}`;
  } catch (e) {
    return `${origin}|${message}`;
  }
};

const buildPayload = (error, { origin, componentStack } = {}) => {
  const payload = {
    origin: origin || "render",
    message: messageOf(error),
    stack: safeString(error?.stack) || null,
    component_stack: safeString(componentStack) || null,
    url: null,
    route_path: null,
    context: {},
  };

  try {
    payload.url = (window.location?.href ?? "").slice(0, 500) || null;
    payload.route_path = (window.location?.pathname ?? "").slice(0, 255) || null;
  } catch (e) {
    /* jsdom o entornos sin window: se envia sin URL */
  }

  try {
    // Solo contexto de apoyo: la identidad real la resuelve el backend del token.
    const user = getStorageUser();
    payload.context = {
      user_id: user?.id ?? null,
      profile_id: user?.profile_id ?? null,
      viewport: `${window.innerWidth ?? 0}x${window.innerHeight ?? 0}`,
      language: navigator?.language ?? null,
      referrer: (document?.referrer ?? "").slice(0, 500) || null,
    };
  } catch (e) {
    payload.context = {};
  }

  return payload;
};

/**
 * Reporta un error. Devuelve una promesa con el folio (`reference`) que asigno
 * el backend, o null si no se pudo enviar. La promesa nunca se rechaza.
 */
export const reportError = async (error, options = {}) => {
  try {
    const origin = options.origin || "render";
    const payload = buildPayload(error, options);

    // Diagnostico local: siempre, aunque el envio falle.
    console.error(`[errorReporter:${origin}]`, error, options.componentStack ?? "");

    const fingerprint = fingerprintOf(origin, payload.message);
    if (reported.has(fingerprint)) return null;
    if (sent >= MAX_REPORTS_PER_SESSION) return null;

    reported.add(fingerprint);
    sent += 1;

    const response = await reportErrorLog(payload);
    return response?.reference ?? null;
  } catch (e) {
    // Silencio deliberado: el reporter no puede propagar fallos.
    return null;
  }
};

/**
 * Engancha los errores que ocurren fuera del arbol de React (callbacks,
 * timers, promesas de axios). El ErrorBoundary no los ve.
 */
export const installGlobalErrorHandlers = () => {
  if (handlersInstalled) return;
  if (typeof window === "undefined") return;

  handlersInstalled = true;

  window.addEventListener("error", (event) => {
    reportError(event?.error ?? event?.message, { origin: "window" });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event?.reason, { origin: "promise" });
  });
};

export default reportError;
